import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase/config';

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Get the most recent comic date among all comics in the user's collection.
 * Prefers non-future release/cover dates when available.
 * Returns a Date object, or null if no comics are found.
 * @param {string} userEmail
 * @returns {Promise<Date|null>}
 */
export const getLastComicDate = async (userEmail) => {
  const db = firestore;
  let latestPastDate = null;
  let latestAnyDate = null;
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  try {
    const heroesRef = collection(db, `users/${userEmail}/heroes/`);
    const heroSnapshot = await getDocs(heroesRef);

    const heroPromises = [];

    heroSnapshot.forEach((heroDoc) => {
      // Use document ID (which is hero.name) for the Firestore path
      const heroName = heroDoc.id;
      const seriesRef = collection(db, `users/${userEmail}/heroes/${heroName}/series`);

      const heroPromise = getDocs(seriesRef).then((seriesSnapshot) => {
        const seriesPromises = [];

        seriesSnapshot.forEach((seriesDoc) => {
          // Use document ID (the sanitized series title) for the Firestore path
          const seriesId = seriesDoc.id;
          const comicsRef = collection(
            db,
            `users/${userEmail}/heroes/${heroName}/series/${seriesId}/comics`
          );

          const comicsPromise = getDocs(comicsRef).then((comicsSnapshot) => {
            comicsSnapshot.forEach((comicDoc) => {
              const comicData = comicDoc.data().data;
              const dateCandidates = [
                comicData && (comicData.storeDate || comicData.store_date),
                comicData && (comicData.coverDate || comicData.cover_date),
              ];

              dateCandidates.forEach((value) => {
                const parsedDate = parseDateValue(value);
                if (!parsedDate) return;

                if (!latestAnyDate || parsedDate > latestAnyDate) {
                  latestAnyDate = parsedDate;
                }

                if (parsedDate <= today && (!latestPastDate || parsedDate > latestPastDate)) {
                  latestPastDate = parsedDate;
                }
              });
            });
          }).catch(() => { /* skip unreadable comics sub-collections */ });
          seriesPromises.push(comicsPromise);
        });

        return Promise.all(seriesPromises);
      }).catch(() => { /* skip unreadable series sub-collections */ });

      heroPromises.push(heroPromise);
    });

    await Promise.all(heroPromises);
  } catch (err) {
    console.warn('getLastComicDate: could not read collection dates, using default', err);
  }

  // Prefer latest non-future date to avoid future-dated cover months collapsing
  // the "latest releases" lookback window.
  return latestPastDate || latestAnyDate;
};

/**
 * Build a set of Comic Vine volume IDs that the user tracks, and a map from
 * volume ID to publisher name (sourced from the series documents in Firestore).
 * @param {string} userEmail
 * @returns {Promise<{ volumeIds: Set<number>, publisherMap: Map<number, string> }>}
 */
export const getTrackedVolumeInfo = async (userEmail) => {
  const db = firestore;
  const volumeIds = new Set();
  const publisherMap = new Map(); // Map<volumeId, publisherName>
  const heroMap = new Map(); // Map<volumeId, heroName>
  const seriesTitleMap = new Map(); // Map<volumeId, seriesTitle as stored in Firebase>

  try {
    const heroesRef = collection(db, `users/${userEmail}/heroes/`);
    const heroSnapshot = await getDocs(heroesRef);

    const heroPromises = [];

    heroSnapshot.forEach((heroDoc) => {
      // Use document ID (which is hero.name) for the Firestore path
      const heroName = heroDoc.id;
      const seriesRef = collection(db, `users/${userEmail}/heroes/${heroName}/series`);

      const heroPromise = getDocs(seriesRef).then((seriesSnapshot) => {
        seriesSnapshot.forEach((seriesDoc) => {
          const seriesData = seriesDoc.data().data;
          if (seriesData) {
            const vid = seriesData.comicVineId || seriesData.id;
            if (vid) {
              const numId = Number(vid);
              volumeIds.add(numId);
              heroMap.set(numId, heroName);
              seriesTitleMap.set(numId, seriesDoc.id);
              if (seriesData.publisher && seriesData.publisher !== 'Unknown') {
                publisherMap.set(numId, seriesData.publisher);
              }
            }
          }
        });
      }).catch(() => { /* skip unreadable series sub-collections */ });

      heroPromises.push(heroPromise);
    });

    await Promise.all(heroPromises);
  } catch (err) {
    console.warn('getTrackedVolumeInfo: could not read tracked volumes, returning empty results', err);
  }
  return { volumeIds, publisherMap, heroMap, seriesTitleMap };
};

/**
 * Build a set of Comic Vine volume IDs that the user tracks.
 * @param {string} userEmail
 * @returns {Promise<Set<number>>}
 */
export const getTrackedVolumeIds = async (userEmail) => {
  const { volumeIds } = await getTrackedVolumeInfo(userEmail);
  return volumeIds;
};
